import { useState, useEffect } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { format } from 'date-fns';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtFull = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
const today = () => new Date().toISOString().split('T')[0];

const LOAN_TYPES = ['mortgage', 'car', 'student', 'personal', 'credit_card', 'other'];
const LOAN_ICONS = { mortgage: '⌂', car: '◎', student: '◎', personal: '▭', credit_card: '▭', other: '≣' };
const TYPE_COLORS = { mortgage: '#1a5fa8', car: '#2d7a4f', student: '#7a5f28', personal: '#8b3a8b', credit_card: '#b83232', other: '#6b6560' };

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid var(--stone)', borderRadius: 8, padding: '10px 14px', boxShadow: 'var(--shadow)' }}>
      <div style={{ fontSize: 11, color: 'var(--mid)', marginBottom: 5 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ fontSize: 12.5, color: p.color, marginBottom: 2 }}>
          {p.name}: {fmt(p.value)}
        </div>
      ))}
    </div>
  );
};

export default function LoansPage() {
  const [loans, setLoans] = useState([]);
  const isMobile = useIsMobile();
  const [summary, setSummary] = useState(null);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('overview');

  // Lump sum simulator
  const [lumpSum, setLumpSum] = useState('');
  const [lumpStrategy, setLumpStrategy] = useState('reduce_term');
  const [lumpResult, setLumpResult] = useState(null);
  const [lumpLoading, setLumpLoading] = useState(false);

  const simulateLumpSum = async (loan) => {
    if (!lumpSum || parseFloat(lumpSum) <= 0) return toast.error('Enter a lump sum amount');
    setLumpLoading(true);
    setLumpResult(null);
    try {
      const t = (() => { try { return localStorage.getItem('token'); } catch { return sessionStorage.getItem('token'); } })();
      const res = await fetch(`/api/loans/${loan.id}/lump-sum`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ lump_sum: parseFloat(lumpSum), strategy: lumpStrategy }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setLumpResult(data);
    } catch (e) { toast.error(e.message); }
    finally { setLumpLoading(false); }
  };

  const [form, setForm] = useState({
    name: '', loan_type: 'mortgage', principal: '',
    annual_rate: '', term_months: '', start_date: today(),
    current_balance: '', notes: '',
  });

  const load = async () => {
    const [sum] = await Promise.all([api.getLoansSummary()]);
    setSummary(sum);
    setLoans(sum.loans || []);
  };

  useEffect(() => { load(); }, []);

  const openSchedule = async (loan) => {
    setSelectedLoan(loan);
    setTab('schedule');
    const data = await api.getLoanSchedule(loan.id);
    setSchedule(data);
  };

  const save = async () => {
    if (!form.name || !form.principal || !form.annual_rate || !form.term_months) {
      toast.error('Fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      await api.addLoan({
        name: form.name,
        loan_type: form.loan_type,
        principal: parseFloat(form.principal),
        annual_rate: parseFloat(form.annual_rate),
        term_months: parseInt(form.term_months),
        start_date: form.start_date,
        current_balance: form.current_balance ? parseFloat(form.current_balance) : null,
        notes: form.notes || null,
      });
      toast.success('Loan added!');
      setShowModal(false);
      setForm({ name: '', loan_type: 'mortgage', principal: '', annual_rate: '', term_months: '', start_date: today(), current_balance: '', notes: '' });
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteLoan = async (id) => {
    if (!window.confirm('Remove this loan?')) return;
    await api.deleteLoan(id);
    toast.success('Removed');
    load();
  };

  // Build amortization chart data from schedule
  const scheduleChartData = schedule?.schedule?.map(s => ({
    name: format(new Date(s.date), 'MMM yy'),
    Principal: s.principal,
    Interest: s.interest,
  })) || [];

  const balanceData = schedule?.schedule?.map(s => ({
    name: format(new Date(s.date), 'MMM yy'),
    Balance: s.balance,
  })) || [];

  return (
    <div className="animate-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="page-eyebrow">Debt</div>
          <div className="page-title">Loans</div>
          <div className="page-subtitle" style={{ marginBottom: 0 }}>Track every loan, see amortization, and understand your total debt load</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Add Loan</button>
      </div>

      {/* Summary KPIs */}
      {summary && (
        <div className="grid-3 mb-6">
          <div className="card">
            <div className="stat-label">Total Outstanding Debt</div>
            <div className="stat-value red">{fmt(summary.total_balance)}</div>
            <div className="stat-sub">{summary.loan_count} active loan{summary.loan_count !== 1 ? 's' : ''}</div>
          </div>
          <div className="card">
            <div className="stat-label">Monthly Debt Payments</div>
            <div className="stat-value gold">{fmt(summary.total_monthly_payment)}</div>
            <div className="stat-sub">Recurring monthly obligation</div>
          </div>
          <div className="card">
            <div className="stat-label">Total Interest Remaining</div>
            <div className="stat-value" style={{ color: 'var(--dim)' }}>{fmt(summary.total_interest_remaining)}</div>
            <div className="stat-sub">Cost of borrowing</div>
          </div>
        </div>
      )}

      <div className="tabs">
        <button className={`tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>All Loans</button>
        {selectedLoan && (
          <button className={`tab ${tab === 'schedule' ? 'active' : ''}`} onClick={() => setTab('schedule')}>
            {selectedLoan.name}
          </button>
        )}
        {selectedLoan && (
          <button className={`tab ${tab === 'simulator' ? 'active' : ''}`} onClick={() => { setTab('simulator'); setLumpResult(null); }}>
            Lump Sum Simulator
          </button>
        )}
      </div>

      {/* Overview tab */}
      {tab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {loans.length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: '48px', borderStyle: 'dashed' }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>◫</div>
              <div style={{ fontWeight: 500, marginBottom: 5 }}>No loans added yet</div>
              <div style={{ color: 'var(--mid)', fontSize: 13 }}>Add your mortgage, car loan, or any other debt to track amortization</div>
            </div>
          )}
          {loans.map(loan => {
            const paid = loan.principal - loan.current_balance;
            const pctPaid = loan.principal > 0 ? (paid / loan.principal) * 100 : 0;
            return (
              <div key={loan.id} className="card">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-3">
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: TYPE_COLORS[loan.loan_type] + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                      {LOAN_ICONS[loan.loan_type]}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 15 }}>{loan.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--mid)', textTransform: 'capitalize', marginTop: 1 }}>
                        {loan.loan_type} · {loan.annual_rate}% APR · {loan.term_months} months
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="btn btn-sm btn-ghost" onClick={() => openSchedule(loan)}>View Schedule →</button>
                    <button className="btn btn-sm btn-ghost" onClick={() => { setSelectedLoan(loan); setTab('simulator'); setLumpResult(null); }}>Lump Sum</button>
                    <button className="btn btn-sm btn-danger" onClick={() => deleteLoan(loan.id)}>×</button>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: 16, marginBottom: 14 }}>
                  {[
                    ['Balance', fmt(loan.current_balance), 'var(--red)'],
                    ['Monthly Payment', fmtFull(loan.monthly_payment), 'var(--ink)'],
                    ['Original Principal', fmt(loan.principal), 'var(--dim)'],
                    ['Total Interest', fmt(loan.total_interest), 'var(--dim)'],
                    ['Payoff Date', format(new Date(loan.payoff_date), 'MMM yyyy'), 'var(--gold)'],
                  ].map(([label, val, color]) => (
                    <div key={label}>
                      <div className="stat-label">{label}</div>
                      <div style={{ fontSize: 15, fontWeight: 500, color }}>{val}</div>
                    </div>
                  ))}
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--mid)', marginBottom: 5 }}>
                    <span>{pctPaid.toFixed(1)}% paid off</span>
                    <span>{loan.months_remaining} months remaining</span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${pctPaid}%`, background: TYPE_COLORS[loan.loan_type] }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Schedule tab */}
      {tab === 'schedule' && selectedLoan && schedule && (
        <div className="animate-in">
          <div className="grid-2 mb-6">
            <div className="card">
              <div className="section-title mb-4">Principal vs Interest Over Time</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={scheduleChartData} barSize={8}>
                  <XAxis dataKey="name" tick={{ fill: 'var(--mid)', fontSize: 10 }} axisLine={false} tickLine={false} interval={2} />
                  <YAxis hide />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11, color: 'var(--dim)' }} />
                  <Bar dataKey="Principal" fill={TYPE_COLORS[selectedLoan.loan_type]} radius={[2,2,0,0]} />
                  <Bar dataKey="Interest" fill={TYPE_COLORS[selectedLoan.loan_type] + '55'} radius={[2,2,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card">
              <div className="section-title mb-4">Outstanding Balance Over Time</div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={balanceData}>
                  <defs>
                    <linearGradient id="loanGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={TYPE_COLORS[selectedLoan.loan_type]} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={TYPE_COLORS[selectedLoan.loan_type]} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" tick={{ fill: 'var(--mid)', fontSize: 10 }} axisLine={false} tickLine={false} interval={2} />
                  <YAxis hide />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="Balance" stroke={TYPE_COLORS[selectedLoan.loan_type]} strokeWidth={2} fill="url(#loanGrad)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card" style={{ padding: 0 }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--stone)' }}>
              <div className="section-title" style={{ marginBottom: 0 }}>Amortization Schedule (first 24 months)</div>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th><th>Date</th><th>Status</th>
                    <th style={{ textAlign: 'right' }}>Payment</th>
                    <th style={{ textAlign: 'right' }}>Principal</th>
                    <th style={{ textAlign: 'right' }}>Interest</th>
                    <th style={{ textAlign: 'right' }}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.schedule.map(row => (
                    <tr key={row.month} style={{ background: row.is_current ? 'var(--off)' : '' }}>
                      <td style={{ color: 'var(--mid)', fontSize: 12 }}>{row.month}</td>
                      <td style={{ fontSize: 13 }}>{format(new Date(row.date), 'MMM d, yyyy')}</td>
                      <td>
                        {row.is_past && <span className="badge badge-stone">Past</span>}
                        {row.is_current && <span className="badge badge-yellow">Current</span>}
                        {!row.is_past && !row.is_current && <span style={{ color: 'var(--mid)', fontSize: 12 }}>Upcoming</span>}
                      </td>
                      <td className="text-right" style={{ fontWeight: 500 }}>{fmtFull(row.payment)}</td>
                      <td className="text-right amount-pos">{fmtFull(row.principal)}</td>
                      <td className="text-right amount-neg">{fmtFull(row.interest)}</td>
                      <td className="text-right" style={{ color: 'var(--ink2)' }}>{fmt(row.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Add loan modal */}
      {tab === 'simulator' && selectedLoan && (
        <div className="animate-in">
          <div className="card mb-4" style={{ background: 'var(--off)', border: '1px solid var(--stone)' }}>
            <div style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 4 }}>Simulating on</div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>{selectedLoan.name}</div>
            <div style={{ fontSize: 13, color: 'var(--mid)', marginTop: 2 }}>
              Balance: {fmt(selectedLoan.current_balance)} · Payment: {fmt(selectedLoan.monthly_payment)}/mo · {selectedLoan.months_remaining} months left
            </div>
          </div>

          <div className="card mb-4">
            <div className="section-title mb-4">Lump Sum Payment Simulator</div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Lump Sum Amount ($)</label>
                <input
                  className="input"
                  type="number"
                  step="100"
                  placeholder="e.g. 5000"
                  value={lumpSum}
                  onChange={e => { setLumpSum(e.target.value); setLumpResult(null); }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Strategy</label>
                <select className="input" value={lumpStrategy} onChange={e => { setLumpStrategy(e.target.value); setLumpResult(null); }}>
                  <option value="reduce_term">Same payment → Pay off earlier</option>
                  <option value="reduce_payment">Same term → Lower monthly payment</option>
                </select>
              </div>
            </div>
            <button className="btn btn-primary" onClick={() => simulateLumpSum(selectedLoan)} disabled={lumpLoading || !lumpSum}>
              {lumpLoading ? 'Calculating…' : 'Simulate'}
            </button>
          </div>

          {lumpResult && (
            <div className="card animate-in">
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', marginBottom: 16, padding: '10px 14px', background: 'var(--accent-glow)', borderRadius: 6 }}>
                {lumpResult.message}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>
                {/* Before */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--mid)', marginBottom: 12 }}>Before lump sum</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[
                      ['Balance', fmt(lumpResult.before.current_balance)],
                      ['Monthly Payment', `${fmtFull(lumpResult.before.monthly_payment)}/mo`],
                      ['Months Remaining', `${lumpResult.before.months_remaining} months`],
                      ['Total Interest Left', fmt(lumpResult.before.total_interest)],
                      ['Payoff Date', new Date(lumpResult.before.payoff_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })],
                    ].map(([label, value]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: '1px solid var(--stone)' }}>
                        <span style={{ color: 'var(--mid)' }}>{label}</span>
                        <span style={{ fontWeight: 500 }}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* After */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--green)', marginBottom: 12 }}>After ${parseFloat(lumpSum).toLocaleString()} lump sum</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[
                      ['New Balance', fmt(lumpResult.after.new_balance)],
                      ['Monthly Payment', `${fmtFull(lumpResult.after.monthly_payment)}/mo`],
                      ['Months Remaining', `${lumpResult.after.months_remaining} months`],
                      ['Total Interest Left', fmt(lumpResult.after.total_interest)],
                      ['New Payoff Date', new Date(lumpResult.after.payoff_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })],
                    ].map(([label, value]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0', borderBottom: '1px solid var(--stone)' }}>
                        <span style={{ color: 'var(--mid)' }}>{label}</span>
                        <span style={{ fontWeight: 500, color: 'var(--green)' }}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Savings summary */}
              <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                <div className="card" style={{ background: '#f0faf4', border: '1px solid #b3d9c2', padding: '14px 18px' }}>
                  <div className="stat-label">Interest Saved</div>
                  <div className="stat-value green">{fmt(lumpResult.interest_saved)}</div>
                </div>
                <div className="card" style={{ background: '#f0faf4', border: '1px solid #b3d9c2', padding: '14px 18px' }}>
                  <div className="stat-label">{lumpStrategy === 'reduce_term' ? 'Months Saved' : 'Monthly Savings'}</div>
                  <div className="stat-value green">
                    {lumpStrategy === 'reduce_term'
                      ? `${lumpResult.months_saved} months`
                      : fmtFull(lumpResult.before.monthly_payment - lumpResult.after.monthly_payment) + '/mo'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-title">Add Loan</div>

            <div className="form-group">
              <label className="form-label">Loan Name *</label>
              <input className="input" placeholder='e.g. "Home Mortgage", "Honda CR-V"' value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>

            <div className="form-group">
              <label className="form-label">Loan Type</label>
              <select className="input" value={form.loan_type} onChange={e => setForm(f => ({ ...f, loan_type: e.target.value }))}>
                {LOAN_TYPES.map(t => <option key={t} value={t}>{LOAN_ICONS[t]} {t.charAt(0).toUpperCase() + t.replace('_', ' ').slice(1)}</option>)}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Original Principal ($) *</label>
                <input className="input" type="number" step="0.01" placeholder="300000" value={form.principal} onChange={e => setForm(f => ({ ...f, principal: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Current Balance (optional)</label>
                <input className="input" type="number" step="0.01" placeholder="Leave blank to auto-compute" value={form.current_balance} onChange={e => setForm(f => ({ ...f, current_balance: e.target.value }))} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 16, marginTop: 16 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Annual Rate (%) *</label>
                <input className="input" type="number" step="0.01" placeholder="6.50" value={form.annual_rate} onChange={e => setForm(f => ({ ...f, annual_rate: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Term (months) *</label>
                <input className="input" type="number" placeholder="360 = 30yr" value={form.term_months} onChange={e => setForm(f => ({ ...f, term_months: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">First Payment Date</label>
                <input className="input" type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
              </div>
            </div>

            {/* Live payment preview */}
            {form.principal && form.annual_rate && form.term_months && (
              <div className="card-off mt-4" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12.5, color: 'var(--dim)' }}>Estimated monthly payment</span>
                <span style={{ fontSize: 18, fontWeight: 600, color: 'var(--ink)', fontFamily: 'var(--font-d)', fontStyle: 'italic' }}>
                  {(() => {
                    try {
                      const p = parseFloat(form.principal);
                      const r = parseFloat(form.annual_rate) / 100 / 12;
                      const n = parseInt(form.term_months);
                      if (r === 0) return `$${(p/n).toFixed(2)}`;
                      const pmt = p * r * Math.pow(1+r,n) / (Math.pow(1+r,n)-1);
                      return `$${pmt.toFixed(2)}/mo`;
                    } catch { return '—'; }
                  })()}
                </span>
              </div>
            )}

            <div className="form-group mt-4">
              <label className="form-label">Notes (optional)</label>
              <input className="input" placeholder="Lender, account number…" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>

            <div className="flex gap-3 justify-between mt-4">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Add Loan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
